// Per-browser recency tracking for the nav rail's Dashboards > Recent folder
// and the unseen-update shine. "Recently accessed" is a viewer-side concept,
// so it lives in localStorage (no API surface): the detail route records a
// visit, and the rail merges visit times with server-side createdAt — a
// dashboard is "recent" if it was recently opened OR recently created.
//
// Exposed as a tiny external store (subscribe + cached snapshot) so the rail
// can `useSyncExternalStore` and re-render the moment a visit is recorded —
// e.g. the shine must clear as soon as the dashboard is opened, not on the
// next navigation.

const storageKey = 'cortex.dashboards.visits';

type Listener = () => void;
const listeners = new Set<Listener>();

/** Snapshot cache — useSyncExternalStore needs referential stability between changes. */
let cache: Record<string, number> | null = null;

export function recordDashboardVisit(dashboardId: string): void {
  try {
    const visits = { ...readDashboardVisits(), [dashboardId]: Date.now() };
    localStorage.setItem(storageKey, JSON.stringify(visits));
    cache = visits;
  } catch {
    // Storage unavailable (private mode, quota) — recency degrades to createdAt,
    // but keep the in-memory snapshot so this session still reacts.
    cache = { ...(cache ?? {}), [dashboardId]: Date.now() };
  }
  listeners.forEach((fn) => fn());
}

export function readDashboardVisits(): Record<string, number> {
  cache ??= readFromStorage();
  return cache;
}

export function subscribeDashboardVisits(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function readFromStorage(): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (typeof parsed !== 'object' || parsed === null) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => typeof v === 'number'),
    ) as Record<string, number>;
  } catch {
    return {};
  }
}
